import Phaser from "phaser";
import { setScrollableItemInputEnabled, syncScrollableItemState, type ScrollableContentItem } from "./scrollableListItemState.js";

/** ScrollableList 생성 옵션. */
export interface ScrollableListOptions {
  readonly width: number;
  readonly height: number;
  readonly barWidth?: number;
  readonly barColor?: number;
  readonly trackColor?: number;
  readonly trackAlpha?: number;
  readonly barAlpha?: number;
  readonly scrollSpeed?: number;
  readonly thumbMinHeight?: number;
  /** Viewport background hit zone interactivity; disable for draggable child content. */
  readonly viewportInteractive?: boolean;
  /**
   * Virtualization 가시 판정 여유(px). 항목 1개 높이(예: 타일 TILE_H) 권장 — 부분 가시 항목 클립 깜빡임
   * 방지 + 스크롤 팝인 완충. 화면 밖 항목은 `setVisible(false)`로 렌더/GeometryMask CPU 클립 비용 제거.
   * **미설정 시 Infinity** → 모든 항목 항상 visible(기존 동작, 회귀 0). 항목 많은 리스트만 명시 권장.
   */
  readonly itemMargin?: number;
}

export const SCROLLABLE_LIST_EVENT = {
  Scroll: "scrollableList:scroll",
} as const;

/** 클릭과 드래그 스크롤을 분기하는 이동 임계(px). 초과 시 드래그 제스처로 승격(셀 클릭 억제). */
const DRAG_THRESHOLD = 8;
/** 스크롤바 thumb의 좌우 히트 영역 패딩(px). 시각 두께는 유지하되 터치 영역만 확대. */
const THUMB_HIT_PAD = 10;

/**
 * Phaser 3 범용 스크롤 컨테이너 — GeometryMask 클리핑 + 휠/터치 드래그 + 드래그 가능한 스크롤바 thumb.
 *
 * Virtualization: `itemMargin` 지정 시 viewport 밖 콘텐츠 항목을 `setVisible(false)`로 컬링한다 —
 * off-screen 항목이 100+개여도 매 스크롤 프레임 렌더/마스크 비용이 보이는 ~6~10개로 줄어든다(모바일 렉 방지).
 * 가시 판정은 순수 함수 `isItemVisible`(scrollVirtualize.ts)에 위임(SSoT·테스트 cover).
 *
 * 사용법:
 * ```ts
 * const list = new ScrollableList(scene, x, y, { width: 200, height: 300, itemMargin: 52 });
 * scene.add.existing(list);
 * list.addContent(myText, 10, 20);
 * list.setContentHeight(totalH);
 * ```
 */
export class ScrollableList extends Phaser.GameObjects.Container {
  private readonly contentItems: ScrollableContentItem[] = [];
  private readonly contentItemByObject = new Map<Phaser.GameObjects.GameObject, ScrollableContentItem>();
  private readonly frozen = new Set<Phaser.GameObjects.GameObject>();
  private _scrollY = 0;
  private _contentH = 0;
  private vw: number;
  private vh: number;
  private readonly geoMask: Phaser.Display.Masks.GeometryMask;
  private readonly maskGfx: Phaser.GameObjects.Graphics;
  private readonly track: Phaser.GameObjects.Rectangle;
  private readonly thumb: Phaser.GameObjects.Rectangle;
  private readonly zone: Phaser.GameObjects.Zone;
  private readonly _scrollSpeed: number;
  private readonly _thumbMinH: number;
  private readonly _itemMargin: number;
  private readonly _viewportInteractive: boolean;
  private contentInputEnabled = true;

  // scene-level pointerdown 기반 드래그 — 셀(interactive)이 zone 입력을 가로채도 viewport 히트로 직접 판정.
  private _dragCandidate = false;
  private _dragMoved = false;
  private _dragStartY = 0;
  private _dragScrollStart = 0;
  /** 외부 드래그(merge/fusion 셀 reparent 드래그) 중 scene 드래그 스크롤을 억제하는 seam. */
  private _suppressed = false;
  private _thumbDragging = false;
  private _thumbDragStartY = 0;
  private _thumbDragScrollStart = 0;
  /** 직전 thumb 높이 — 변경 시에만 hit area를 재설정해 매 스크롤 프레임 setInteractive 재호출(렉)을 피한다. */
  private _lastThumbH = -1;

  constructor(scene: Phaser.Scene, x: number, y: number, opts: ScrollableListOptions) {
    super(scene, x, y);
    this.vw = opts.width;
    this.vh = opts.height;
    this._scrollSpeed = opts.scrollSpeed ?? 36;
    this._thumbMinH = opts.thumbMinHeight ?? 12;
    // 미설정 시 Infinity → isItemVisible이 항상 true(모든 항목 visible, 기존 동작 보존).
    this._itemMargin = opts.itemMargin ?? Number.POSITIVE_INFINITY;
    this._viewportInteractive = opts.viewportInteractive ?? true;

    const barW = opts.barWidth ?? 4;
    const barColor = opts.barColor ?? 0x6a6f9a;
    const trackColor = opts.trackColor ?? 0x1a1c2e;
    const trackAlpha = opts.trackAlpha ?? 0.5;
    const barAlpha = opts.barAlpha ?? 0.7;

    // Mask — world-space Graphics (not in display list)
    this.maskGfx = scene.make.graphics({});
    this.maskGfx.fillRect(x, y, this.vw, this.vh);
    this.geoMask = this.maskGfx.createGeometryMask();
    // ⚠ 마스크는 Container(this) 1개에만. 상위 렌더 루프(Phaser `WebGLRenderer.render`·`ContainerWebGLRenderer`의
    // `child.mask`)가 this 렌더 시 mask.preRenderWebGL→자식 전체 렌더→postRenderWebGL로 **자식 전체를 1회 stencil
    // 클립**한다(WebGL 소스 확인). RectMask.ts 주석의 "동적 자식에 전파 안 함"은 자식의 `.mask` 프로퍼티 자동설정
    // 부재를 말함 — Container 자신의 mask가 렌더 시 전체 클립하는 것과는 별개 메커니즘. 항목마다 개별 setMask하면
    // 모바일 GPU에서 stencil 설정(colorMask/enable/stencilFunc)이 항목 수만큼 반복돼 렉(CDP 실측 43% — 전투탭 0%).
    this.setMask(this.geoMask);

    // Interaction zone (local coords, center-origin)
    this.zone = scene.add
      .zone(this.vw / 2, this.vh / 2, this.vw, this.vh)
      .setDepth(0);
    if (this._viewportInteractive) this.zone.setInteractive();
    this.add(this.zone);

    // Scrollbar track + thumb (local coords)
    const barX = this.vw - barW / 2 - 1;
    this.track = scene.add
      .rectangle(barX, this.vh / 2, barW, this.vh, trackColor)
      .setAlpha(trackAlpha)
      .setDepth(10)
      .setVisible(false);
    this.add(this.track);

    this.thumb = scene.add
      .rectangle(barX, 0, barW, 20, barColor)
      .setAlpha(barAlpha)
      .setDepth(11)
      .setInteractive({ useHandCursor: true })
      .setVisible(false);
    this.add(this.thumb);

    // --- Input handlers ---
    this._onWheel = this._onWheel.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);

    scene.input.on("wheel", this._onWheel);
    // pointerdown은 scene-level — zone.on("pointerdown")은 셀(interactive)이 위에서 가로채면 발동 안 하므로
    // viewport 히트를 직접 판정해 카드 위에서도 드래그 스크롤이 먹게 한다.
    scene.input.on("pointerdown", this._onPointerDown);
    scene.input.on("pointermove", this._onPointerMove);
    scene.input.on("pointerup", this._onPointerUp);

    this.thumb.on("pointerdown", (p: Phaser.Input.Pointer) => {
      this._thumbDragging = true;
      this._thumbDragStartY = p.y;
      this._thumbDragScrollStart = this._scrollY;
    });
  }

  // ── public API ───────────────────────────────────────

  get maxScroll(): number {
    return Math.max(0, this._contentH - this.vh);
  }

  get scrollY(): number {
    return this._scrollY;
  }

  /**
   * 직전 pointer 제스처가 드래그 스크롤(이동 임계 초과)이었는가 — 셀 클릭 핸들러가 드래그 후의 우발 클릭을
   * 억제하는 가드용. pointerup 시점에 유효하며 다음 pointerdown에서 false로 리셋된다.
   */
  get consumedDragGesture(): boolean {
    return this._dragMoved;
  }

  /**
   * 외부 드래그(merge/fusion 셀 reparent 드래그) 중 scene 드래그 스크롤을 일시 억제한다 — Phaser draggable
   * 셀(16px 임계)과 scene 드래그(8px 임계)가 8~16px 구간에서 겹쳐 동시 발동하는 충돌을 차단.
   */
  suppressDragScroll(on: boolean): void {
    this._suppressed = on;
    if (on) this._dragCandidate = false;
  }

  get contentMask(): Phaser.Display.Masks.GeometryMask {
    return this.geoMask;
  }

  get viewWidth(): number {
    return this.vw;
  }

  get viewHeight(): number {
    return this.vh;
  }

  /**
   * 뷰포트 위치/크기 변경(레이아웃 펼치기·리사이즈) — 마스크 Graphics(world space)·zone·스크롤바를
   * 함께 재배치한다. 콘텐츠 항목(lx/ly)은 보존하고 가시성만 재적용한다.
   */
  resize(x: number, y: number, width: number, height: number): void {
    this.setPosition(x, y);
    this.vw = width;
    this.vh = height;
    // 마스크는 display list 밖 world-space Graphics — Container 이동에 안 따라오므로 직접 재그린다.
    this.maskGfx.clear();
    this.maskGfx.fillRect(x, y, width, height);
    const barW = this.track.width;
    const barX = this.vw - barW / 2 - 1;
    this.zone.setPosition(this.vw / 2, this.vh / 2).setSize(this.vw, this.vh);
    this.track.setPosition(barX, this.vh / 2).setSize(barW, this.vh);
    this.thumb.setPosition(barX, this.thumb.y);
    // 뷰포트 높이 축소 시 _scrollY가 새 maxScroll을 초과할 수 있어 클램프(stale 스크롤 오프셋 방지).
    this._scrollY = Math.min(this._scrollY, this.maxScroll);
    this._refreshVisibility();
    this._updateThumb();
  }

  setContentHeight(h: number): void {
    this._contentH = h;
    this.scrollTo(Math.min(this._scrollY, this.maxScroll));
    // scrollTo가 조기 반환(scrollY 불변)해도 신규/변경 항목의 가시성을 강제 재적용.
    this._refreshVisibility();
    this._updateThumb();
  }

  addContent(obj: Phaser.GameObjects.GameObject, lx: number, ly: number): void {
    const item = { obj, lx, ly };
    this.contentItems.push(item);
    this.contentItemByObject.set(obj, item);
    // 개별 setMask 안 함 — Container(this) 마스크가 자식 전체를 1회 stencil로 클립(렉 방지, 위 생성자 참조).
    // (드래그 중 scene 직속으로 reparent되는 셀만 scrollListDragHandle이 개별 setMask로 복원 — 그땐 this 자식이 아니라서.)
    this.add(obj);
    setScrollableItemInputEnabled(obj, this.contentInputEnabled);
    // 가시성 + 위치를 한 번에 적용(viewport 밖이면 setVisible(false) + setPosition 생략).
    this._applyItemVisibility(item);
  }

  /**
   * 콘텐츠 좌표 재배치 — 방향(RTL) 전환 시 destroy/recreate 없이 lx/ly를 교체하고 즉시 재배치한다.
   * 직접 setPosition은 다음 scrollTo가 추적된 lx로 덮어쓰므로, contentItems의 lx/ly를 함께 갱신해야 한다.
   * @param positions 재배치할 (obj, 새 lx, 새 ly) 목록. contentItems에 없는 obj는 무시.
   */
  relayoutContent(
    positions: ReadonlyArray<{ obj: Phaser.GameObjects.GameObject; lx: number; ly: number }>,
  ): void {
    for (const p of positions) {
      const item = this.contentItemByObject.get(p.obj);
      if (!item) continue;
      item.lx = p.lx;
      item.ly = p.ly;
      // frozen 항목은 _applyItemVisibility가 내부에서 skip(드래그 reparent 보호) — lx/ly만 갱신됨.
      this._applyItemVisibility(item);
    }
  }

  clearContent(): void {
    for (const item of this.contentItems) item.obj.destroy();
    this.contentItems.length = 0;
    this.contentItemByObject.clear();
    this.frozen.clear();
    // 스크롤 위치(_scrollY)는 보존한다 — 콘텐츠 제거는 스크롤 리셋 책임이 없고,
    // 직후 호출되는 setContentHeight가 scrollTo(min(_scrollY, maxScroll))로 새 범위에 clamp한다.
    // (250ms 주기 rebuild되는 합성/팔레트 탭에서 드래그 중 toppest로 튕기던 버그 방지)
    this._contentH = 0;
    this._updateThumb();
  }

  scrollTo(value: number): void {
    const clamped = Phaser.Math.Clamp(value, 0, this.maxScroll);
    if (clamped === this._scrollY) return;
    this._scrollY = clamped;
    for (const item of this.contentItems) this._applyItemVisibility(item);
    this._updateThumb();
    this.emit(SCROLLABLE_LIST_EVENT.Scroll, this._scrollY);
  }

  /** 드래그 중인 아이템을 스크롤 재배치에서 제외. */
  freezeItem(obj: Phaser.GameObjects.GameObject): void {
    this.frozen.add(obj);
  }

  /** freezeItem 해제. */
  unfreezeItem(obj: Phaser.GameObjects.GameObject): void {
    this.frozen.delete(obj);
  }

  setVisible(v: boolean): this {
    super.setVisible(v);
    this.setActive(v);
    this.contentInputEnabled = v;
    this._refreshVisibility();
    this.zone.setActive(v);
    this.track.setActive(v);
    this.thumb.setActive(v);
    if (v) {
      if (this._viewportInteractive) this.zone.setInteractive();
      this.thumb.setInteractive({ useHandCursor: true });
    } else {
      this._dragCandidate = false;
      this._thumbDragging = false;
      this.zone.disableInteractive();
      this.thumb.disableInteractive();
    }
    this._updateThumb();
    return this;
  }

  // ── lifecycle ────────────────────────────────────────

  preDestroy(): void {
    this.scene.input.off("wheel", this._onWheel);
    this.scene.input.off("pointerdown", this._onPointerDown);
    this.scene.input.off("pointermove", this._onPointerMove);
    this.scene.input.off("pointerup", this._onPointerUp);
    this.maskGfx.destroy();
  }

  // ── private ──────────────────────────────────────────

  private _applyItemVisibility(item: ScrollableContentItem): void {
    syncScrollableItemState(item, this.frozen, this._scrollY, this.vh, this._itemMargin, this.contentInputEnabled);
  }

  /** 전체 항목 가시성 재적용 — scrollTo 조기 반환을 우회해야 하는 setContentHeight 등에서 사용. */
  private _refreshVisibility(): void {
    for (const item of this.contentItems) this._applyItemVisibility(item);
  }

  private _onWheel(
    _p: Phaser.Input.Pointer,
    _gos: unknown[],
    _dx: number,
    dy: number,
  ): void {
    if (!this.visible || this.maxScroll <= 0) return;
    const lx = _p.x - this.x;
    const ly = _p.y - this.y;
    if (lx < 0 || lx > this.vw || ly < 0 || ly > this.vh) return;
    this.scrollTo(this._scrollY + (dy > 0 ? this._scrollSpeed : -this._scrollSpeed));
  }

  /**
   * scene-level pointerdown — viewport 안 + thumb 위가 아니면 드래그 후보로 등록한다.
   * `_dragMoved`를 여기서 리셋 — 셀의 pointerup 가드(consumedDragGesture)가 직전 제스처 값을 읽은 뒤
   * 새 제스처 시작 시점에 초기화되므로, GameObject/scene pointerup 발동 순서와 무관하게 안전하다.
   */
  private _onPointerDown(p: Phaser.Input.Pointer): void {
    this._dragMoved = false;
    if (!this.visible || this.maxScroll <= 0 || this._suppressed) {
      this._dragCandidate = false;
      return;
    }
    const lx = p.x - this.x;
    const ly = p.y - this.y;
    const inView = lx >= 0 && lx <= this.vw && ly >= 0 && ly <= this.vh;
    const onThumb = this._isOnThumb(lx, ly);
    this._dragCandidate = inView && !onThumb;
    if (this._dragCandidate) {
      this._dragStartY = p.y;
      this._dragScrollStart = this._scrollY;
    }
  }

  /** thumb 로컬 bounds 히트 판정(히트 패딩 포함) — thumb 위에서 시작한 down은 thumb 드래그가 우선. */
  private _isOnThumb(lx: number, ly: number): boolean {
    if (!this.thumb.visible) return false;
    const halfW = this.thumb.width / 2 + THUMB_HIT_PAD;
    const halfH = this.thumb.height / 2;
    return (
      lx >= this.thumb.x - halfW &&
      lx <= this.thumb.x + halfW &&
      ly >= this.thumb.y - halfH &&
      ly <= this.thumb.y + halfH
    );
  }

  private _onPointerMove(p: Phaser.Input.Pointer): void {
    if (!this.visible) return;
    if (this._dragCandidate) {
      const delta = this._dragStartY - p.y;
      if (!this._dragMoved && Math.abs(delta) >= DRAG_THRESHOLD) this._dragMoved = true;
      if (this._dragMoved) this.scrollTo(this._dragScrollStart + delta);
    }
    if (this._thumbDragging) {
      const dy = p.y - this._thumbDragStartY;
      const thumbH = this.thumb.height;
      const trackRange = this.vh - thumbH;
      if (trackRange > 0) {
        this.scrollTo(this._thumbDragScrollStart + (dy / trackRange) * this.maxScroll);
      }
    }
  }

  private _onPointerUp(): void {
    // _dragMoved는 리셋하지 않는다 — 셀 pointerup 가드가 읽을 수 있게 다음 pointerdown까지 유지.
    this._dragCandidate = false;
    this._thumbDragging = false;
    // suppress 자동 해제 — scene-level pointerup은 정상 up뿐 아니라 touchcancel 후 사이클에서도 도달하므로,
    // 외부(scrollListDragHandle)가 dragend 누락(OS 인터럽트)으로 해제를 못 해도 suppress가 영구 잔류하지 않는다.
    this._suppressed = false;
  }

  private _updateThumb(): void {
    const show = this.visible && this.maxScroll > 0;
    this.track.setVisible(show);
    this.thumb.setVisible(show);
    if (!show) return;
    const ratio = this.vh / (this.vh + this.maxScroll);
    const thumbH = Math.max(this._thumbMinH, this.vh * ratio);
    const frac = this._scrollY / this.maxScroll;
    const thumbY = thumbH / 2 + frac * (this.vh - thumbH);
    this.thumb.setSize(this.thumb.width, thumbH).setPosition(this.thumb.x, thumbY);
    // 히트 영역 확대 — 시각 두께(barWidth)는 유지하되 좌우 ±THUMB_HIT_PAD 넓혀 5px 스크롤바도 손가락으로
    // 쉽게 잡히게 한다. center-origin Rectangle 기준 좌상단 offset = (-w/2, -thumbH/2).
    // ⚠ thumbH가 바뀔 때만 재설정 — _updateThumb은 매 scrollTo(스크롤 프레임)마다 불리므로 무조건 setInteractive를
    // 재호출하면 InputPlugin 히트영역 갱신이 프레임마다 누적돼 렉. thumbH는 콘텐츠/뷰포트 변경 시에만 바뀐다.
    if (thumbH !== this._lastThumbH) {
      this._lastThumbH = thumbH;
      const hitW = this.thumb.width + THUMB_HIT_PAD * 2;
      this.thumb.setInteractive(
        new Phaser.Geom.Rectangle(-hitW / 2, -thumbH / 2, hitW, thumbH),
        Phaser.Geom.Rectangle.Contains,
      );
    }
  }
}
